import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { observable } from "mobx";
import { FormGroup, Button, Icon, Intent, TextArea, InputGroup } from "@blueprintjs/core";
import Toast from "helpers/toaster";
import FormDrawer from "components/FormDrawer";
import { FormSection, Choice, FieldError, MoreOptions } from "components/FormBits";

const TERMS = ["Net 15", "Net 30", "Net 45", "Net 60", "Net 90", "Upon Completion"].map((term) => ({
  value: term,
  label: term === "Upon Completion" ? "On completion" : term,
}));

const blank = (value) => value === "" || value === null || value === undefined;

// Field -> input id, in the order they appear (the first with a problem gets focus).
const FIELD_IDS = {
  name: "new-client-name",
  phone: "new-client-phone",
  email: "new-client-email",
  legal_name: "new-client-legal-name",
};

const focusField = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.focus();
    element.scrollIntoView({ block: "center" });
  }
};

@inject("clientStore")
@observer
class AddClientModal extends Component {
  @observable errors = {};

  // Cancelling clears the form too: the inputs come back empty on the next
  // open, so the store must not keep values the user can't see.
  handleCloseClientDailog() {
    this.props.clientStore.clearClientObject();
    this.errors = {};
    this.props.onClose();
  }

  setField(field, value) {
    const { newClient } = this.props.clientStore;
    if (!newClient.object) {
      newClient.object = {};
    }
    newClient.object[field] = value;
    if (this.errors[field]) {
      this.errors = { ...this.errors, [field]: null };
    }
  }

  validate(object) {
    const errors = {};
    if (blank(object.name)) errors.name = "Enter the client's name.";
    if (blank(object.phone)) errors.phone = "Enter a phone number.";
    if (blank(object.email)) errors.email = "Enter an email address.";
    if (blank(object.legal_name)) errors.legal_name = "Enter the legal name, or use “Use client name”.";
    return errors;
  }

  createClient() {
    const object = this.props.clientStore.newClient.object || {};

    const errors = this.validate(object);
    this.errors = errors;
    const first = Object.keys(FIELD_IDS).find((field) => errors[field]);
    if (first) {
      focusField(FIELD_IDS[first]);
      return;
    }

    this.props.clientStore
      .createClient()
      .then(this.onSuccessfulAddClient.bind(this))
      .catch(this.onFailAddClient.bind(this));
  }

  onSuccessfulAddClient(response) {
    this.errors = {};
    this.props.onSuccess(response.data.data);
  }

  onFailAddClient(error) {
    Toast.show({
      icon: "warning-sign",
      message: error.response.data.errors.join(", "),
      intent: Intent.DANGER
    });
    console.log(error);
  }

  textInput(field, { label, placeholder, type = "text", required, autoComplete, inputMode }) {
    // The panel stays mounted on pages where no client object is loaded.
    const object = this.props.clientStore.newClient.object || {};
    const id = FIELD_IDS[field] || `new-client-${field}`;
    const error = this.errors[field];
    return (
      <FormGroup
        label={label}
        labelFor={id}
        labelInfo={required ? <span className="fb-required" aria-hidden="true">*</span> : undefined}
        className={error ? "has-error" : undefined}
      >
        <input
          id={id}
          type={type}
          className="bp3-input bp3-fill"
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          value={object[field] || ""}
          onChange={(event) => this.setField(field, event.target.value)}
        />
        <FieldError id={`${id}-error`}>{error}</FieldError>
      </FormGroup>
    );
  }

  render() {
    const { isLoading } = this.props.clientStore.newClient;
    const object = this.props.clientStore.newClient.object || {};
    const close = this.handleCloseClientDailog.bind(this);
    const errors = this.errors;
    const hasAddresses = !blank(object.invoice_to) || !blank(object.ship_to);
    const hasExtras = !blank(object.fax) || !blank(object.description);

    return (
      <FormDrawer
        size="min(760px, 100vw)"
        isOpen={this.props.isOpen}
        title="New client"
        icon="person"
        onClose={close}
        busy={isLoading}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={isLoading} />
            <Button
              intent={Intent.PRIMARY}
              icon="add"
              text="Create client"
              loading={isLoading}
              onClick={this.createClient.bind(this)}
            />
          </>
        }
      >
        <FormSection title="Contact" hint="Who they are and how to reach them.">
          {this.textInput("name", {
            label: "Name",
            placeholder: "e.g. Jane Cooper or Cooper Dental",
            required: true,
            autoComplete: "off",
          })}
          <div className="row">
            <div className="col-6">
              {this.textInput("phone", {
                label: "Phone",
                placeholder: "e.g. (555) 123-4567",
                type: "tel",
                required: true,
                autoComplete: "off",
                inputMode: "tel",
              })}
            </div>
            <div className="col-6">
              {this.textInput("email", {
                label: "Email",
                placeholder: "name@example.com",
                type: "email",
                required: true,
                autoComplete: "off",
              })}
            </div>
          </div>
          <MoreOptions label="Fax and notes" open={hasExtras}>
            {this.textInput("fax", { label: "Fax", placeholder: "e.g. (555) 123-4568", type: "tel", autoComplete: "off" })}
            <FormGroup label="Notes about this client" labelFor="new-client-description">
              <TextArea
                id="new-client-description"
                className="bp3-fill"
                placeholder="e.g. Gate code 1234; prefers morning visits"
                rows={3}
                value={object.description || ""}
                onChange={(event) => this.setField("description", event.target.value)}
              />
            </FormGroup>
          </MoreOptions>
        </FormSection>

        <FormSection title="Billing" hint="How invoices are addressed and when they're due.">
          <FormGroup
            label="Legal name"
            labelFor={FIELD_IDS.legal_name}
            labelInfo={<span className="fb-required" aria-hidden="true">*</span>}
            helperText={errors.legal_name ? undefined : "As it appears on invoices."}
            className={errors.legal_name ? "has-error" : undefined}
          >
            <InputGroup
              id={FIELD_IDS.legal_name}
              placeholder="e.g. Cooper Dental LLC"
              autoComplete="off"
              aria-required={true}
              aria-invalid={errors.legal_name ? true : undefined}
              aria-describedby={errors.legal_name ? `${FIELD_IDS.legal_name}-error` : undefined}
              value={object.legal_name || ""}
              onChange={(event) => this.setField("legal_name", event.target.value)}
              rightElement={
                blank(object.name) ? undefined : object.legal_name === object.name ? (
                  <span className="fb-inline-action is-done">
                    <Icon icon="tick" iconSize={12} />
                    Same as name
                  </span>
                ) : (
                  <button
                    type="button"
                    className="fb-inline-action"
                    onClick={() => this.setField("legal_name", object.name)}
                  >
                    Use client name
                  </button>
                )
              }
            />
            <FieldError id={`${FIELD_IDS.legal_name}-error`}>{errors.legal_name}</FieldError>
          </FormGroup>

          <Choice
            label="Payment terms"
            name="terms"
            value={object.terms}
            onChange={(value) => this.setField("terms", value)}
            options={TERMS}
          />

          <MoreOptions label="Invoice and ship-to addresses" open={hasAddresses}>
            <FormGroup label="Invoice to" labelFor="new-client-invoice-to">
              <TextArea
                id="new-client-invoice-to"
                className="bp3-fill"
                placeholder={"e.g. Accounts payable\n12 Main St, Springfield, IL 62701"}
                rows={3}
                value={object.invoice_to || ""}
                onChange={(event) => this.setField("invoice_to", event.target.value)}
              />
            </FormGroup>
            <div className="bp3-form-group">
              <div className="fb-label-row">
                <label className="fb-label" htmlFor="new-client-ship-to">Ship to</label>
                {blank(object.invoice_to) ? null : object.ship_to === object.invoice_to ? (
                  <span className="fb-inline-action is-done">
                    <Icon icon="tick" iconSize={12} />
                    Same as invoice to
                  </span>
                ) : (
                  <button
                    type="button"
                    className="fb-inline-action"
                    onClick={() => this.setField("ship_to", object.invoice_to)}
                  >
                    Use invoice to address
                  </button>
                )}
              </div>
              <TextArea
                id="new-client-ship-to"
                className="bp3-fill"
                placeholder="Where parts and paperwork are delivered"
                rows={3}
                value={object.ship_to || ""}
                onChange={(event) => this.setField("ship_to", event.target.value)}
              />
            </div>
          </MoreOptions>
        </FormSection>
      </FormDrawer>
    );
  }
}

export default AddClientModal;
